import { ColumnDef } from '@tanstack/react-table'
import LongText from '@/components/long-text'
import { Case, Person } from '../data/schema'
import { DataTableRowActions } from './data-table-row-actions'
import i18n from '@/i18n'
import { NameBadge } from '@/components/badge-gen'
import { Badge } from '@/components/ui/badge'
import { DataTableColumnHeader } from '@/components/data-table-column-header'
import { Link } from '@tanstack/react-router'
import { formatDistanceToNow } from 'date-fns'
import { ar } from 'date-fns/locale'


import { Star } from 'lucide-react'
import { update_case } from '@/api/case'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { toast } from '@/hooks/use-toast'

const FavoriteCell = ({ row }: { row: { original: Case } }) => {
	const [isFavorite, setIsFavorite] = useState(row.original.is_favorite || false)
	const [loading, setLoading] = useState(false)

	const toggleFavorite = async (e: React.MouseEvent) => {
		e.stopPropagation()
		const newValue = !isFavorite
		setIsFavorite(newValue) // Optimistic update
		setLoading(true)

		try {
			await update_case(row.original.id, { is_favorite: newValue } as any)
			toast({
				title: newValue ? i18n.t('added_to_favorites') : i18n.t('removed_from_favorites'),
				description: newValue ? i18n.t('case_marked_as_favorite') : i18n.t('case_unmarked_as_favorite'),
			})
		} catch (error) {
			setIsFavorite(!newValue) // Revert on error
			toast({
				title: i18n.t('error'),
				description: i18n.t('failed_to_update_favorite'),
				variant: 'destructive',
			})
		} finally {
			setLoading(false)
		}
	}

	return (
		<Button
			variant="ghost"
			size="icon"
			className="h-8 w-8 p-0"
			onClick={toggleFavorite}
			disabled={loading}
		>
			<Star
				className={`h-4 w-4 ${isFavorite ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'}`}
			/>
			<span className="sr-only">{isFavorite ? i18n.t('remove_from_favorites') : i18n.t('add_to_favorites')}</span>
		</Button>
	)
}

export const columns: ColumnDef<Case>[] = [
	{
		accessorKey: 'is_favorite',
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title="" />
		),
		cell: FavoriteCell,
		enableSorting: false,
		enableHiding: false,
	},
	{
		accessorKey: 'status',
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title={i18n.t('case_status')} />
		),
		cell: ({ row }) => {
			const { status } = row.original

			const statusColors: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
				active: "default",
				audience: "secondary",
				delibere: "secondary",
				suspended: "outline",
				closed: "default", // Using secondary for closed to distinguish from primary action
				archived: "outline",
			};

			const variant = status && statusColors[status] ? statusColors[status] : "secondary";

			/* Fallback for legacy archived_at check if status is null but archived_at exists */
			if (!status && row.original.archived_at) {
				return <Badge variant="outline">{i18n.t('case_status.archived')}</Badge>
			}

			return (
				<div className='flex items-center gap-2'>
					{status ? (
						<Badge variant={variant}>
							{i18n.t(`case_status.${status}`)}
						</Badge>
					) : (
						<span className="text-muted-foreground">-</span>
					)}
				</div>
			)
		},
	},
	{
		accessorKey: 'case_number_full',
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title={i18n.t('case_number_full')} />
		),
		cell: ({ row }) => {
			const { year, code, number } = row.original
			const formattedId = [year, code, number].filter(Boolean).join('/')
			return <div className='w-fit text-nowrap'>{formattedId}</div>
		},
	},
	{
		accessorKey: 'name',
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title={i18n.t('case_name')} />
		),
		cell: ({ row }) => {
			const { name } = row.original
			return <LongText className='max-w-36'>{name}</LongText>
		},
	},
	{
		accessorKey: 'case_type',
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title={i18n.t('case_type')} />
		),
		cell: ({ row }) => {
			const { type } = row.original
			return (
				<div className='flex space-x-2'>
					{type && <NameBadge name={type.name} />}
				</div>
			)
		},
	},
	{
		accessorKey: 'client',
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title={i18n.t('client')} />
		),
		cell: ({ row }) => {
			const client = row.getValue('client') as Person
			return (
				<div className='w-fit text-nowrap'>

					{client.name}
				</div>
			)
		},
	},
	{
		accessorKey: 'assigned_to',
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title={i18n.t('assigned_to')} />
		),
		cell: ({ row }) => {
			const assignedTo = row.getValue('assigned_to') as Person | undefined
			if (!assignedTo) return ''
			return (
				<Link
					to="/users/$id"
					params={{ id: assignedTo.id }}
					className='w-fit text-nowrap hover:underline'
					onClick={(e) => e.stopPropagation()} // Stop event propagation
				>
					{assignedTo.name}
				</Link>
			)
		},
	},

	{
		accessorKey: 'updated_at',
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title={i18n.t('last_update')} />
		),
		cell: ({ row }) => {
			const date = new Date(row.getValue('updated_at') as string)
			return (
				<>
					<div className='w-fit text-nowrap'>
						{date.toLocaleDateString('en-GB')}
					</div>
					<span className="w-fit text-nowrap text-xs text-muted-foreground">({formatDistanceToNow(date, { addSuffix: true, locale: ar })})</span>
				</>
			)
		},
	},
	{
		id: 'actions',
		cell: DataTableRowActions,
	},
]
